import React, { useEffect, useState, useRef, useContext } from "react";
import { useTheme } from "../../context/ThemeContext";
import { isStageOrDevEnv, getPayabliSrc, isAuthenticatedViaCookie, logout} from "../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { expressCheckoutConfig, payabliConfigACH, payabliConfigCard } from "./Payabli";
import { normaliseLookupInput, resolveLookupValue } from "../../utils/lookupInput";
import { useDispatch, useSelector } from "react-redux";
import { hideSideBar } from "../../store/sidebarSlice";
import { RootState } from "../../store";
import { updatePatientPostPayment, getPendingTransactions, createPatientRecurringScheduleApi } from "../../utils/apis";
import { telHref } from "../common/TransitionNotice";
import { computeInstallmentPlan } from "./installmentPlan";
import "./LinkPayment.css";
import { FailurePopup } from '../shared/FailurePopup';
import VerificationModal from './VerificationModal';
import GNVerificationModal from './GNVerificationModal';
import UserInfoHeader from './UserInfoHeader';
import PaymentAccordion from './PaymentAccordion';
import SkeletonLoader from './SkeletonLoader';
import PaymentResultModal from './modals/PaymentResultModal';
import PaymentHeader from './PaymentHeader';
import Sidebar from './Sidebar';
import MobileMenuButton from './MobileMenuButton';
import { InPortalLayoutContext } from './PortalLayout';
import BillsSummary from './BillsSummary';
import { visibleBills } from './billsTable';

// Add type definitions at the top of the file, after imports
// PayabliComponent type definitions
interface PayabliComponentInstance {
  cleanup?: () => void;
  destroy?: () => void;
  updateConfig: (config: any) => void;
  payabliExec: (method: string, data: any) => void;
}

export const PAYMENT_TABS = {
  CARD: "card",
  BANK_DEBIT: "bank_debit",
  WALLET: 'wallet'
}

const SCHEDULE_FREQUENCIES = {
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly'
}


// Function to map frequency to API format
const mapFrequencyToAPI = (frequency: string): string => {
  switch (frequency) {
    case SCHEDULE_FREQUENCIES.WEEKLY:
      return 'WEEKLY';
    case SCHEDULE_FREQUENCIES.BIWEEKLY:
      return 'BIWEEKLY';
    case SCHEDULE_FREQUENCIES.MONTHLY:
      return 'MONTHLY';
    case SCHEDULE_FREQUENCIES.QUARTERLY:
      return 'QUARTERLY';
    default:
      return 'WEEKLY';
  }
};

// SUPPORTED_RECURRING_FREQUENCIES, MAX_INSTALLMENTS, and computeInstallmentPlan now live in
// ./installmentPlan — extracted so the plan math (per-charge, derived N, cadence guard) is unit-tested
// in isolation instead of buried in this component.

const LinkPayment: React.FC = () => {
  
  const { theme } = useTheme();
  const [apiLoading, setApiLoading] = useState(false); 
  const [error, setError] = useState<string | null>(null);
  // Update the ref type
  const payabliComponentRef = useRef<PayabliComponentInstance | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [selectedPaymentTab, setSelectedPaymentTab] = useState(PAYMENT_TABS.CARD);
  const [isPaying, setIsPaying] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); 

  const [payabliToken, setPayabliToken] = useState<string | undefined>(undefined);
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState<number>(0);
  /**
   * Which EHR answered the lookup. Consumed to SUPPRESS online payment for a Cerner account.
   *
   * The public capture endpoint allocates Meditech-only: for a Cerner guarantor it finds no accounts,
   * marks the parent COMPLETED, emails "payment received" and writes no allocation — so nothing is
   * stamped source_ehr=CERNER, the lockbox never sees it, and the money is charged with no route to
   * Cerner. SF-Backend#772 refuses that server-side; this stops the patient reaching a pay button
   * only to be rejected, and tells them what to do instead.
   */
  const [sourceEhr, setSourceEhr] = useState<string | null>(null);
  const onlinePaymentUnavailable = sourceEhr === 'CERNER';
  const [payabliEntryId, setPayabliEntryId] = useState<string | undefined>(undefined);
  
  const validateUserAndFetchPayments = async  (guarantorLastName?: string, guarantorNumber?: string) => {
    try {
      const response = await getPendingTransactions(guarantorLastName, guarantorNumber);
      // Handle new patient accounts API response format
      if (response.status === 200 && response.data && response.data.accounts.length > 0) {
        
        const firstAccount = response?.data?.accounts[0];
        const userInfo = {
          name: `${firstAccount.guarantorNameFirst || ''} ${firstAccount.guarantorNameLast || ''}`.trim(),
          gn: firstAccount.guarantorNumber || guarantorNumber || 'N/A',
          email: firstAccount.guarantorEmail || undefined,
          phone: undefined, // Not available in new API
          guarantorLastName: firstAccount.guarantorNameLast || undefined
        };
  
        console.log('response?.data?.payabliToken', response?.data?.payabliToken);
        console.log('response?.data?.payabliEntityId', response?.data?.payabliEntityId);
        setPayabliToken(response?.data?.payabliToken);
        setPayabliEntryId(response?.data?.payabliEntityId);
        setPendingPaymentAmount(response?.data?.pendingPaymentAmount || 0);
        setSourceEhr(response?.data?.sourceEhr ?? null);
        // Transform patient accounts to payment-like structure for UI compatibility
        const transformedPayments = response.data?.accounts.map((account: any) => ({
          totalDueAmount: account.accountPatientBalance ?? 0,
          id: account.accountId,
          amount: account.accountPatientBalance ?? 0,
          status: 'pending', // Default status for patient accounts
          dueDate: account.accountServiceDatetime,
          invoiceNumber: account.accountNumber,
          description: `Account ${account.accountNumber} - ${account.accountLocation || 'Medical Services'}`,
          payabliToken: undefined, // Not available in new API
          payabliEntryId: undefined, // Not available in new API
          currency: 'USD', // Default currency
          paymentType: 'ONE_TIME', // Default payment type
          statementDate: account.accountServiceDatetime,
          paymentDate: undefined,
          paymentIdentity: {
            firstName: account.guarantorNameFirst,
            lastName: account.guarantorNameLast,
            uniqueId: account.guarantorNumber,
            email: account.guarantorEmail,
            guarantorLastName: account.guarantorNameLast
          },
          // Additional patient account specific fields
          patientInfo: {
            patientNameFirst: account.patientNameFirst,
            patientNameLast: account.patientNameLast,
            patientNameMiddle: account.patientNameMiddle,
            // patientBirthdate (DOB) intentionally omitted — not shown to patients (lean-safe shape)
            patientRelationshipToGuarantor: account.patientRelationshipToGuarantor
          },
          accountDetails: {
            // Only the balance the patient owes + where — the internal ledger breakup
            // (accountBalance / insurance / billed-charge totals) is never surfaced to patients.
            accountPatientBalance: account.accountPatientBalance,
            accountLocation: account.accountLocation
          }
        }));
        
        return {
          valid: true,
          user: userInfo,
          payments: transformedPayments,
          payabliToken: response?.data?.payabliToken,
          payabliEntryId: response?.data?.payabliEntityId
        };
      } 
  
      return {
        valid: true,
        user: 'N/A',
        payments: [],
        payabliToken: undefined,
        payabliEntryId: undefined
      };
    } catch (error) {
      console.error('Error validating user:', error);
      return { valid: false };
    }
  };


  // Add refs for the state variables
  const scheduledPaymentRef = useRef({
    isScheduled: false,
    frequency: SCHEDULE_FREQUENCIES.WEEKLY,
    startDate: '',
    amountPerPayment: '',
  });
  const [scheduledPayment, setScheduledPayment] = useState(scheduledPaymentRef.current);

  const pendingPaymentsRef = useRef<any[]>([]);
  const currentPaymentRef = useRef<any>(null);

  // Update ref values when state changes
  useEffect(() => {
    scheduledPaymentRef.current = scheduledPayment;
  }, [scheduledPayment]);
  
  const [modals, setModals] = useState({
    showVerify: false,
    showGN: false,
    showError: false,
    errorMsg: '',
    shouldRetryAfterError: false,
  });
  
  const [userInfo, setUserInfo] = useState<any>(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(null);
  const [selectedPaymentForProcessing, setSelectedPaymentForProcessing] = useState<any>(null);
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isAuthenticated");
    if(isLoggedIn) {
      setIsAuthenticated(() => {
        return true;
      });
    }
  }, []);
  
  const [pageReady, setPageReady] = useState(false); 
  
  const [resultModal, setResultModal] = useState({
    isOpen: false,
    isSuccess: false,
    message: '',
  });
  
  const dispatch = useDispatch()
  const isOpenSidbar = useSelector((state: RootState) => state.sidebar.isOpen);

  const location = useLocation();
  const navigate = useNavigate();
  const inLayout = useContext(InPortalLayoutContext);
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const lastNameFromQuery = queryParams.get("lastName");
  const gnFromQuery = queryParams.get("gn");

  const [lastNameInput, setLastNameInput] = useState('');
  const [gnInput, setGnInput] = useState('');
  

  // Normalised on the way IN as well as on submit. A link that was shared or bookmarked with a stray
  // space in it would otherwise refill the field with the same bad value and fail again.
  useEffect(() => {
    if (lastNameFromQuery) {
      setLastNameInput(normaliseLookupInput(lastNameFromQuery));
    }
    if (gnFromQuery) {
      setGnInput(normaliseLookupInput(gnFromQuery));
    }
  }, [lastNameFromQuery, gnFromQuery]);

  useEffect(() => {
    if (modals.shouldRetryAfterError && (lastNameFromQuery || lastNameInput)) {
      const timer = setTimeout(() => {
        if (id) {
          handleVerifyLastName();
        } else if (gnFromQuery || gnInput) {
          handleVerifyGN();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    setPageReady(true);
  }, []);

  useEffect(() => {
    if(isAuthenticated && !id) {
      const handleAuthenticated = async () => {
        setModals(prev => ({ ...prev, showVerify: false, showGN: false }));
        const res = await validateUserAndFetchPayments(undefined, undefined);
        if (res.valid) {
          setIsUserVerified(true);
          setUserInfo(res.user);
          pendingPaymentsRef.current = res.payments || [];
        } else {
          clearURLParams(); 
          setModals(prev => ({ ...prev, showVerify: false, showGN: true }));
        }
      }
      handleAuthenticated();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isUserVerified) {
      if (id) {
        if (lastNameFromQuery) {
          handleVerifyLastNameWithQueryParams();
        } else {
          setModals(prev => ({ ...prev, showVerify: true, showGN: false }));
        }
      } else if (!isAuthenticated) {
        if (lastNameFromQuery && gnFromQuery) {
          handleVerifyGNWithQueryParams();
        } else {
          setModals(prev => ({ ...prev, showGN: true, showVerify: false }));
        }
      }
    }
  }, [id, isUserVerified, lastNameFromQuery, gnFromQuery, isAuthenticated]);

  const getMinStartDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // The plan total is ALWAYS the full balance (sum of the guarantor's pending dues). `fallback` (the
  // per-charge) is used only when there are no pending rows, so N degrades to 1 rather than divide by
  // a zero total. Single source so the pre-vault guard and the create call can never diverge.
  const getBillTotal = (fallback: number): number =>
    (pendingPaymentsRef.current || []).reduce(
      (sum: number, p: any) => sum + (p?.totalDueAmount || p?.amount || 0), 0) || fallback;

  const isScheduledPaymentValid = () => {
    if (!scheduledPaymentRef.current.isScheduled) return true;
    if (!scheduledPaymentRef.current.startDate) return false;
    // Installments-first: the per-charge amount is the amount field — its $10 floor and "<= balance"
    // are enforced by the field itself (UserInfoHeader.isAmountValid). Here we only require a start
    // date and a positive per-charge installment; the charge COUNT is derived, not user-entered.
    const perCharge = parseFloat(scheduledPaymentRef.current.amountPerPayment)
      || (currentPaymentRef.current?.amount || 0);
    return perCharge > 0;
  };

  const updateURLWithParams = (params: { lastName?: string; gn?: string }) => {
    const newQueryParams = new URLSearchParams(location.search);
    
    if (params.lastName) {
      newQueryParams.set('lastName', params.lastName);
    }
    if (params.gn) {
      newQueryParams.set('gn', params.gn);
    }
    
    navigate(`${location.pathname}?${newQueryParams.toString()}`, { replace: true });
  };

  const clearURLParams = () => {
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.delete('lastName');
    newQueryParams.delete('gn');
    
    navigate(`${location.pathname}?${newQueryParams.toString()}`, { replace: true });
  };

  const handleVerifyLastNameWithQueryParams = async () => {
    if (!lastNameFromQuery) return;
    
    setApiLoading(true);
    setError(null);
    try {
      // Normalised: this path AUTO-FIRES from the query string before the patient touches anything,
      // so a statement link carrying a trailing space or zero-width character would fail here and show
      // "Last name is incorrect" on a link that is actually correct.
      const res = await validateUserAndFetchPayments(
        normaliseLookupInput(lastNameFromQuery) || undefined, undefined);
      if (res.valid) {
        setIsUserVerified(true);
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams(); 
        setModals(prev => ({ ...prev, errorMsg: 'Last name does not match our records. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams(); 
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handleVerifyGNWithQueryParams = async () => {
    if (!lastNameFromQuery || !gnFromQuery) return;
    
    setApiLoading(true);
    setError(null);
    try {
      // Same auto-fire path for the GN + last-name link, and the likeliest one to carry pasted junk.
      const res = await validateUserAndFetchPayments(
        normaliseLookupInput(lastNameFromQuery) || undefined,
        normaliseLookupInput(gnFromQuery) || undefined);
      if (res.valid) {
        setIsUserVerified(true);
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
          clearURLParams(); 
        setModals(prev => ({ ...prev, errorMsg: 'Guarantor Number or Last Name is incorrect. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams(); 
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (scheduledPaymentRef.current.startDate) {
      setScheduledPayment(prev => ({ ...prev }));
    }
  }, [scheduledPaymentRef.current.startDate, scheduledPaymentRef.current.frequency]);

  const refreshPaymentData = async () => {
    try {
      
      // Least urgent of the three (after a modal verification the URL already holds the normalised
      // value), but normalised anyway so the rule is "every call site" rather than "the ones we
      // remembered" — which is precisely how the first cut of this fix missed these.
      const lastNameToUse = normaliseLookupInput(lastNameFromQuery) || undefined;
      const gnToUse = normaliseLookupInput(gnFromQuery) || undefined;
      
      if (userInfo) {
        const res = await validateUserAndFetchPayments(
          lastNameToUse, 
          gnToUse
        );
        
        if (res.valid) {
          pendingPaymentsRef.current = res.payments || [];
        }
      } 
      // else if (id) {
      //   // TODO: As of now, it's not required
      //   const res = await validateUserAndFetchPayments(setResultModal, lastNameToUse, undefined, id);
      //   if (res.valid) {
      //     setUserInfo(res.user);
      //     pendingPaymentsRef.current = res.payments || [];
      //   }
      // }
      
      setModals(prev => ({ ...prev, showVerify: false }));
      setModals(prev => ({ ...prev, showGN: false }));
      setModals(prev => ({ ...prev, showError: false }));
    } catch (error) {
      console.error('Error refreshing payment data:', error);
    }
  };


  // Reset scheduled payment state when payments change
  useEffect(() => {
    if (pendingPaymentsRef.current.length > 0) {
      setScheduledPayment({
        isScheduled: false,
        frequency: SCHEDULE_FREQUENCIES.WEEKLY,
        startDate: '',
        amountPerPayment: '',
      });
    }
  }, [pendingPaymentsRef.current]);

  // Add a ref to track if callback is already executed
  const callbackExecutedRef = useRef(false);
  
  // Create callback functions outside component
  const createCallbackFunctions = (isWallet: boolean = false) => {
    const functionCallBackSuccess = async (response: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      callbackExecutedRef.current = true;
      
      try {
        // Get the current scheduled payment state from ref
        const currentScheduledPayment = scheduledPaymentRef.current;
        const isScheduled = currentScheduledPayment.isScheduled;
        const referenceId =  isWallet ? response?.data?.responseData?.referenceId : response?.responseData?.referenceId || '';
        let amount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        
        let scheduledStartDate: string | undefined = undefined;
        let frequency: string | undefined = undefined;
        
        if (isScheduled) {
          scheduledStartDate = currentScheduledPayment.startDate || undefined;
          frequency = mapFrequencyToAPI(currentScheduledPayment.frequency) || undefined;
        }
        const pendingPayment = pendingPaymentsRef.current?.length > 0 ? pendingPaymentsRef.current[0].paymentIdentity : {};

        const payabliResp = isWallet ? response.data : response;
        if (isScheduled) {
          // Custom-dates recurring (System B): the card is already vaulted via payabliExec("method").
          // Create the schedule on the authenticated patient endpoint — the backend binds the
          // guarantor to the logged-in user (identity comes from the JWT, never this body) and our
          // charge-run charges the vaulted method each occurrence (no Payabli-native subscription).
          //
          // The scheduling UI captures an INSTALLMENT plan: the patient picks the per-charge amount +
          // cadence; N is DERIVED so the plan pays off the FULL balance. `amount` here is the patient's
          // per-charge installment (the amount field); the plan total is always the full balance (sum
          // of the guarantor's pending dues). We send the per-occurrence installment + the derived
          // COUNT; the backend derives the end date for N charges AND caps the final charge to the
          // exact remaining balance (ISS-34), so the plan can never over/undercharge or run forever.
          // Vault-completeness guard: the card was vaulted above, but if Payabli returned no
          // stored-method reference, creating the schedule now would 400 (the backend requires
          // storedMethodId) and strand the patient with a saved card + a generic error. Abort with a
          // clear, retryable message instead of firing the doomed create.
          if (!referenceId || !String(referenceId).trim()) {
            console.error('Vault returned no referenceId — aborting recurring-schedule create', response);
            setResultModal({ isOpen: true, isSuccess: false, message: "Your card was saved, but we couldn't set up the payment schedule. Please try again — if it keeps happening, contact support." });
            setIsPaymentDone(true);
            setIsOpen(false);
            setIsPaying(false);
            return;
          }

          const plan = computeInstallmentPlan(getBillTotal(amount), amount, mapFrequencyToAPI(scheduledPaymentRef.current.frequency));
          if (plan.error) {
            // Already enforced pre-vault; belt-and-suspenders. Card is only vaulted here, no charge made.
            setResultModal({ isOpen: true, isSuccess: false, message: plan.error });
            setIsPaymentDone(true);
            setIsOpen(false);
            setIsPaying(false);
            return;
          }

          await createPatientRecurringScheduleApi({
            // Identity (guarantor number + last name) is derived server-side from the JWT — not sent.
            amount: plan.perOccurrenceAmount,
            numberOfPayments: plan.numberOfPayments,
            frequency: plan.apiFrequency,
            scheduledStartDate: scheduledStartDate as string,
            storedMethodId: referenceId,
            paymentAggregatorResponse: payabliResp?.responseData || payabliResp,
          });
        } else {
          await updatePatientPostPayment(
            amount || currentPaymentRef.current?.amount,
            referenceId,
            payabliResp,
            isScheduled,
            scheduledStartDate,
            frequency,
            pendingPayment.guarantorLastName,
            pendingPayment.uniqueId
          );
        }

        if(isScheduled) {
          // Set success modal and ensure it stays visible
          setResultModal({ isOpen: true, isSuccess: true, message: 'Your scheduled payment request was processed successfully!' });
        } else {
          // Set success modal and ensure it stays visible
          setResultModal({ isOpen: true, isSuccess: true, message: 'Your payment request was successful! Your payment will take 2-3 business days to process.' });
        }
        
        // Set states to indicate payment is complete
        setIsPaymentDone(true);
        setIsOpen(false);
        setIsPaying(false);
        
        await refreshPaymentData();
      } catch (error: any) {
        setResultModal({ isOpen: true, isSuccess: false, message: error?.response?.data?.message || 'There was an error processing your payment. Please try again.' });
        setIsPaymentDone(true);
        setIsOpen(false);
        setIsPaying(false);
      } finally {
        setApiLoading(false);
      }
    };

    const functionCallBackError = async (response: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      callbackExecutedRef.current = true;
      
      setResultModal({ isOpen: true, isSuccess: false, message: 'There was an error processing your request. Please try again.' });
      setIsPaymentDone(true);
      setIsOpen(false);
      setIsPaying(false);
      setApiLoading(false);
    };

    return { functionCallBackSuccess, functionCallBackError };
  };

  const closePopup = () => {
    setIsOpen(false);
    // Reset callback execution flag
    callbackExecutedRef.current = false;
    // Reset selected payment tab to default
    setSelectedPaymentTab(PAYMENT_TABS.CARD);
    // Cleanup the component and its callbacks
    if (payabliComponentRef.current) {
      try {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // Ensure we remove all event listeners and references
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
      } catch (error) {
        console.error('Error cleaning up payabli component:', error);
      }
      payabliComponentRef.current = null;
    }
  };

  useEffect(() => {
    // Cleanup on component unmount
    return () => {
      closePopup();
    };
  }, []);

  useEffect(() => {
    if(isOpenSidbar) dispatch(hideSideBar())
  }, [isOpenSidbar, dispatch])

  useEffect(() => {
    if (!payabliComponentRef?.current) return;

    try {
      // Cleanup existing instance before updating config
      if (payabliComponentRef.current) {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
      }

      if (selectedPaymentTab === PAYMENT_TABS.CARD) {
        // @ts-ignore
        payabliComponentRef.current.updateConfig(payabliConfigCard);
      } else if(selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT) {
        // @ts-ignore
        payabliComponentRef.current.updateConfig(payabliConfigACH);
      } else {
        // Update expressCheckoutConfig with current payment amount before applying
        const { functionCallBackSuccess, functionCallBackError } = createCallbackFunctions(true);
        const paymentAmount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
        expressCheckoutConfig.functionCallBackSuccess = functionCallBackSuccess;
        expressCheckoutConfig.functionCallBackError = functionCallBackError;
        // @ts-ignore
        payabliComponentRef.current.updateConfig(expressCheckoutConfig);
      }
    } catch (error) {
      console.error('Error updating payabli config:', error);
      // If there's an error, reinitialize the component
      initializePayabliForm(currentPaymentRef.current);
    }
  }, [selectedPaymentTab]);

  // Function to handle payment from UserInfoHeader with paymentIdentityId
  const handleUserHeaderPayNow = async (amount: number, isScheduled: boolean, scheduledPaymentData?: any) => {
    try {
      setApiLoading(true);
      const firstPayment = pendingPaymentsRef.current[0];
      const finalPayment = {
        ...firstPayment,
        firstName: firstPayment.paymentIdentity?.firstName,
        lastName: firstPayment.paymentIdentity?.lastName,
        email: firstPayment.paymentIdentity?.email,
        phoneNumber: firstPayment.paymentIdentity?.phone,
        invoiceNumber: firstPayment.invoiceNumber || firstPayment.paymentIdentity?.uniqueId,
        amount: amount, // Use the amount from UserInfoHeader
        status: firstPayment.status,
        dueDate: firstPayment.dueDate,
        providerName: firstPayment.description || 'Payment for services',
        payabliToken: payabliToken || null,
        payabliEntryId: payabliEntryId || null
      }
      currentPaymentRef.current = finalPayment;

      console.log('scheduledPaymentData', isScheduled, scheduledPaymentData);
      
      // Update scheduled payment state if provided
      if (isScheduled) {
        const newScheduledPayment = {
          isScheduled: true,
          frequency: scheduledPaymentData.frequency,
          startDate: scheduledPaymentData.startDate,
          // Installments-first: the amount field IS the per-charge installment. Pin it here so the
          // create-time computeInstallmentPlan derives N = ceil(fullBalance / perCharge) — the charge
          // COUNT is derived, not user-entered (fixes the single-shot N=1 default).
          amountPerPayment: amount > 0 ? String(amount) : scheduledPaymentData.amountPerPayment,
        };

        console.log('newScheduledPayment', newScheduledPayment);

        setScheduledPayment(newScheduledPayment);
        // STI-1935 / STI-2495: the Payabli component is created synchronously below (loadPayabliScript)
        // before React flushes this setScheduledPayment into scheduledPaymentRef via the [scheduledPayment]
        // effect. configurePayabliConfig reads scheduledPaymentRef to decide temporaryToken, so set the ref
        // now — otherwise the recurring form mounts with the default temporaryToken=true and vaults a
        // one-time token that fails on the 2nd charge with E7007.
        scheduledPaymentRef.current = newScheduledPayment;
      } else {
        // STI-2495 (review B1): a one-time payment must NOT inherit a prior scheduled open's ref. The
        // ref write above has no symmetric reset otherwise — the state->ref effect (:195-196) only fires
        // on setScheduledPayment (which the one-time path never calls), and the [pendingPayments] reset
        // effect (:453-462) is keyed on a ref read and only re-runs incidentally. Both temporaryToken
        // (configurePayabliConfig) and the schedule-vs-charge branch (:865) read this ref, so a stale
        // isScheduled=true would permanently vault a single-use card AND create a schedule for a payment
        // the payer asked to make once. Reset synchronously here, mirroring the scheduled branch.
        const oneTime = { ...scheduledPaymentRef.current, isScheduled: false };
        setScheduledPayment(oneTime);
        scheduledPaymentRef.current = oneTime;
      }

      // Load Payabali script and open popup
      loadPayabliScript(finalPayment);
    } catch (error) {
      console.error('Error fetching payments by paymentIdentityId:', error);
      setError("Failed to load payment information");
    } finally {
      setApiLoading(false);
    }
  };

  const loadPayabliScript = (payment: any) => {
    const paymentToUse = payment || currentPaymentRef.current;
    
    if (!paymentToUse) {
      console.error('No payment data available');
      return;
    }

    // Clean up any existing instance before creating a new one
    if (payabliComponentRef.current) {
      // @ts-ignore
      if (payabliComponentRef.current.cleanup) {
        payabliComponentRef.current.cleanup();
      }
      // @ts-ignore
      if (payabliComponentRef.current.destroy) {
        payabliComponentRef.current.destroy();
      }
      payabliComponentRef.current = null;
    }
    
    if (!document.querySelector('script[src*="payabli.com/component.js"]')) {
      const script = document.createElement("script");
      script.src = getPayabliSrc();
      if(isStageOrDevEnv()) {
        script.dataset.test = "true";
      }
      script.onload = () => initializePayabliForm(paymentToUse);
      document.body.appendChild(script);
    } else {
      initializePayabliForm(paymentToUse);
    }
    setIsOpen(true);
  };

  const initializePayabliForm = (payment: any) => {
    // @ts-ignore
    if (typeof PayabliComponent === "undefined") {
      console.error("Payabli script not loaded.");
      return;
    }

    const currentPayment = payment;
    const paymentIdentityId = currentPayment?.paymentIdentity?.id;
    
    // Reset callback execution flag when initializing new form
    callbackExecutedRef.current = false;
    
    // Create callback functions
    const { functionCallBackSuccess, functionCallBackError } = createCallbackFunctions();

    // Configure the Payabli configs with the callbacks
    const configurePayabliConfig = (config: any) => {
      config.token = currentPayment?.payabliToken;
      config.entryPoint = currentPayment?.payabliEntryId;
      // STI-1935 / STI-2495: EmbeddedMethod defaults temporaryToken=true (a one-time token Payabli drops
      // after the first charge). A scheduled/recurring payment must vault PERMANENTLY so the method
      // survives every cycle; a one-time payment keeps the default. isScheduled is known before this runs
      // (set on scheduledPaymentRef in the open-form handler, before the component is created). Set it
      // explicitly every time because these config objects are shared module-level singletons. Only the
      // methodEmbedded (card/ACH) configs vault a stored method — temporaryToken is meaningless on the
      // expressCheckout/wallet config, so don't stamp it there (STI-2495, review L2).
      if (config.type === "methodEmbedded") {
        config.temporaryToken = scheduledPaymentRef.current?.isScheduled === true ? false : true;
      }
      config.functionCallBackSuccess = functionCallBackSuccess;
      config.functionCallBackError = functionCallBackError;
      config.customerData = {
        firstName: currentPayment?.firstName,
        lastName: currentPayment?.lastName,
        customerNumber: currentPayment?.invoiceNumber,
        billingEmail: currentPayment?.email,
        additionalData: {
          paymentIdentityId: paymentIdentityId,
          // Read straight off the guarantor identity on the fetched accounts. This used to arrive
          // via the bill-summary modal's state: an effect copied paymentIdentity.guarantorLastName /
          // .uniqueId into it purely so this payload could read them back out. Same two values, one
          // less hop, and it no longer depends on a modal that has been removed.
          guarantorLastName: pendingPaymentsRef.current?.[0]?.paymentIdentity?.guarantorLastName,
          guarantorNumber: pendingPaymentsRef.current?.[0]?.paymentIdentity?.uniqueId,
          type: 'PatientPayment'
        },
      };
    };

    // Configure all configs
    configurePayabliConfig(payabliConfigCard);
    configurePayabliConfig(payabliConfigACH);
    configurePayabliConfig(expressCheckoutConfig);
    
    // Configure expressCheckoutConfig with payment amount
    if (expressCheckoutConfig.expressCheckout) {
      const paymentAmount = currentPayment?.amount || 0;
      (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
    }

    try {
      // Cleanup existing instance before creating new one
      if (payabliComponentRef.current) {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
        payabliComponentRef.current = null;
      }

      // Create new instance with the appropriate config based on selected tab
      let initialConfig;
      switch (selectedPaymentTab) {
        case PAYMENT_TABS.BANK_DEBIT:
          initialConfig = payabliConfigACH;
          break;
        case PAYMENT_TABS.WALLET:
          initialConfig = expressCheckoutConfig;
          break;
        default:
          initialConfig = payabliConfigCard;
      }

      // Create new instance
      // @ts-ignore
      payabliComponentRef.current = new PayabliComponent(initialConfig);
    } catch (error) {
      console.error('Error initializing payabli component:', error);
      setResultModal({ 
        isOpen: true, 
        isSuccess: false, 
        message: 'There was an error initializing the payment form. Please try again.' 
      });
    }
  };

  const handlePayClick = (e: any) => {
    // Defence in depth: the modal is not rendered for a Cerner account, but never let a stray path
    // submit money the backend cannot route to Cerner.
    if (onlinePaymentUnavailable) {
      e?.preventDefault?.();
      return;
    }
    e.stopPropagation();
    if (isPaying) return;
    
    setIsPaying(true);
    setIsOpen(false);

    if (payabliComponentRef.current) {
      if (scheduledPaymentRef.current.isScheduled) {
        if (!scheduledPaymentRef.current.startDate) {
          setIsPaying(false);
          return;
        }

        const today = new Date();
        const selectedStartDate = new Date(scheduledPaymentRef.current.startDate);
        if (selectedStartDate < today) {
          setIsPaying(false);
          return;
        }

        const amountToUse = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        // Plan validation (cadence + derived count 1..MAX + positive per-charge) is done by
        // computeInstallmentPlan below, before the vault — no split-specific pre-check needed.

        // Validate amount before proceeding
        if (!amountToUse || amountToUse <= 0) {
          console.error('Invalid amount for scheduled payment:', amountToUse);
          setResultModal({ isOpen: true, isSuccess: false, message: 'Invalid payment amount. Please try again.' });
          setIsPaying(false);
          return;
        }

        // Validate the FULL plan (supported cadence + derived count 1..52 + chargeable per-charge)
        // BEFORE tokenizing, so a rejected plan never leaves an orphaned vaulted card. This is where a
        // bi-weekly selection is caught — #668 would 400 it after the vault otherwise. amountToUse is
        // the per-charge installment; the plan total is always the full balance.
        const plan = computeInstallmentPlan(getBillTotal(amountToUse), amountToUse, mapFrequencyToAPI(scheduledPaymentRef.current.frequency));
        if (plan.error) {
          setResultModal({ isOpen: true, isSuccess: false, message: plan.error });
          setIsPaying(false);
          return;
        }

        const mappedFrequency = mapFrequencyToAPI(scheduledPaymentRef.current.frequency);

        // @ts-ignore
        payabliComponentRef.current.payabliExec("method", {
          paymentDetails: {
            totalAmount: amountToUse,
            serviceFee: 0,
            categories: [{ label: "payment", amount: amountToUse, qty: 1 }],
          },
          scheduleDetails: {
            frequency: mappedFrequency,
            startDate: scheduledPaymentRef.current.startDate,
            // Vault-only exec (tokenize the card); the authoritative schedule is created via
            // createPatientRecurringScheduleApi with the derived plan. Send the derived count +
            // per-charge installment for completeness.
            numberOfPayments: plan.numberOfPayments,
            amountPerPayment: amountToUse,
          }
        });
      } else {
        const paymentAmount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount;
        
        // Validate amount before proceeding
        if (!paymentAmount || paymentAmount <= 0) {
          console.error('Invalid payment amount:', paymentAmount);
          setResultModal({ isOpen: true, isSuccess: false, message: 'Invalid payment amount. Please try again.' });
          setIsPaying(false);
          return;
        }

        const paymentMethod = selectedPaymentTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
        
        // Update expressCheckoutConfig if wallet payment is selected
        if (selectedPaymentTab === PAYMENT_TABS.WALLET) {
          // Create a copy of the original config to update
          const updatedExpressConfig = { ...expressCheckoutConfig };
          
          // Update the expressCheckout amount with the current payment amount
          if (updatedExpressConfig.expressCheckout) {
            (updatedExpressConfig.expressCheckout as any).amount = paymentAmount;
          }
          
          // Update the config before executing payment
          payabliComponentRef.current.updateConfig(updatedExpressConfig);
        }
        
        // @ts-ignore
        payabliComponentRef.current.payabliExec(paymentMethod, {
          paymentDetails: {
            totalAmount: paymentAmount,
            serviceFee: 0,
            categories: [{ label: "payment", amount: paymentAmount, qty: 1 }],
          }
        });
      }
    } else {
      setIsPaying(false);
    }
  };

  const handleVerifyLastName = async () => {
    setApiLoading(true);
    setError(null);
    
    // Normalised before it is sent AND before it is written back to the URL: an invisible character
    // (trailing space, NBSP from a pasted statement) made correct values fail verification.
    // Typed value wins over the query param: the patient may have just corrected it in the modal.
    const lastNameToUse = resolveLookupValue(lastNameInput, lastNameFromQuery);

    try {
      const res = await validateUserAndFetchPayments(lastNameToUse || undefined, undefined);
      if (res.valid) {
        setModals(prev => ({ ...prev, showVerify: false }));
        setIsUserVerified(true);
        setModals(prev => ({ ...prev, shouldRetryAfterError: false }));
        
        updateURLWithParams({ lastName: lastNameToUse });
        
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams();
        setModals(prev => ({ ...prev, errorMsg: 'Last name does not match our records. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams();
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handleVerifyGN = async () => {
    setApiLoading(true);
    setError(null);
    
    // Typed value wins over the query param: the patient may have just corrected it in the modal.
    const lastNameToUse = resolveLookupValue(lastNameInput, lastNameFromQuery);
    const gnToUse = resolveLookupValue(gnInput, gnFromQuery);
    
    try {
      const res = await validateUserAndFetchPayments(lastNameToUse || undefined, gnToUse || undefined);
      if (res.valid) {
        setModals(prev => ({ ...prev, showGN: false }));
        setIsUserVerified(true);
        setModals(prev => ({ ...prev, shouldRetryAfterError: false }));
        updateURLWithParams({ lastName: lastNameToUse, gn: gnToUse });
        
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams();
        setModals(prev => ({ ...prev, errorMsg: 'Guarantor Number or Last Name is incorrect. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) { 
      clearURLParams();
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handlePaymentSelection = (payment: any) => {
    setSelectedPaymentForProcessing(payment);
    setExpandedPaymentId(payment.id);
  };

  const handleHideDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPaymentId(null);
    setSelectedPaymentForProcessing(null);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Display-only view of the accounts (Joel + Olivia, Aug 2026). A guarantor's list is mostly
  // accounts at zero, which made "Total Bills" read 8 when only 3 rows carried a balance, and put a
  // $0.00 line from a 2023 visit in front of a patient who then rang in about it.
  //
  // Filters on totalDueAmount alone, deliberately, because that is the field the Amount column
  // renders: a row with it undefined displays as "$0.00", so it belongs in the hidden set too.
  // The test is `!== 0`, not `> 0`, so credit balances survive. A credit is money owed to the
  // patient and hiding it would look like concealment. Pinned by a test in PaymentAccordion.test.
  //
  // NOT computed with useMemo: this derives from a ref, which does not trigger renders, so a memo
  // keyed on it would serve stale rows. Recomputing per render matches how the old code read
  // pendingPaymentsRef.current.length inline.
  //
  // pendingPaymentsRef.current itself stays unfiltered and unsorted. The payment path reads it for
  // the payer identity and the amount, and none of that should shift because of a display choice.
  const visiblePayments = visibleBills(pendingPaymentsRef.current);

  return (
    <div className={`payment-page  flex flex-row lg:flex-col ${pageReady ? 'payment-page-ready' : 'payment-page-loading'}`}>
      {/* Sidebar — provided by PortalLayout when nested */}
      {!inLayout && (
      <Sidebar
        userInfo={userInfo}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />
      )}
      
      <VerificationModal
        isOpen={modals.showVerify}
        onClose={() => {
          setModals(prev => ({ ...prev, showVerify: false }));
          clearURLParams();
        }}
        onSubmit={handleVerifyLastName}
        lastNameInput={lastNameInput}
        setLastNameInput={setLastNameInput}
        loading={apiLoading}
      />

      <GNVerificationModal
        isOpen={modals.showGN}
        onClose={() => {
          setModals(prev => ({ ...prev, showGN: false }));
          clearURLParams();
        }}
        onSubmit={handleVerifyGN}
        gnInput={gnInput}
        setGnInput={setGnInput}
        lastNameInput={lastNameInput}
        setLastNameInput={setLastNameInput}
        loading={apiLoading}
      />

      <FailurePopup
        message={modals.errorMsg}
        isOpen={modals.showError}
        onClose={() => {
          setModals(prev => ({ ...prev, showError: false }));
          setModals(prev => ({ ...prev, shouldRetryAfterError: true }));
          if (id) {
            setModals(prev => ({ ...prev, showVerify: true }));
          } else {
            setModals(prev => ({ ...prev, showGN: true }));
          }
        }}
      />
      
      <PaymentResultModal
        isOpen={resultModal.isOpen || apiLoading || isPaying}
        isSuccess={resultModal.isSuccess}
        isLoading={!resultModal.isOpen && (apiLoading || isPaying)}
        message={resultModal.message}
        onClose={() => {
          setResultModal({ isOpen: false, isSuccess: false, message: '' });
          if(id){
            window.location.reload();
          }
        }}
      />

      {!isUserVerified && !modals.showError && !userInfo && (
        <div className="">
          {/* Mobile Header — provided by PortalLayout when nested */}
          {!inLayout && (
          <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
              </div>
              <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
            </div>
          </div>
          )}
          
          <div className="lg:ml-80">
            <div className="w-full px-4 py-4">
              <div className="w-full payment-container">
                <SkeletonLoader />
              </div>
            </div>
          </div>
        </div>
      )}

      {userInfo && !modals.showVerify && !modals.showGN && (
        <div className="grow px-4 ">
          {/* Mobile Header — provided by PortalLayout when nested */}
          {!inLayout && (
          <div className="lg:hidden ml-[-16px] mr-[-16px] bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
              <div className="flex items-center">
                <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
              </div>
              <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
            </div>
          </div>
          )}
          
          <div className="w-full">
            <div className="w-full grow py-4">
              <div className="w-full payment-container" data-testid="main-content">
              {visiblePayments.length > 0 ? (
                <>
                  <BillsSummary 
                    totalBills={visiblePayments.length}
                    totalAmountDue={pendingPaymentsRef.current.reduce((sum, payment) => sum + (payment.totalDueAmount || payment.amount || 0), 0)}
                  />
                  
                  {onlinePaymentUnavailable && (
                    /* Says WHY there is no pay button. Without this the patient sees their balance,
                       no way to act on it, and no explanation — worse than being told plainly. */
                    <div
                      className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left"
                      role="status"
                      aria-live="polite"
                      data-testid="online-payment-unavailable"
                    >
                      <p className="text-sm font-semibold text-[#242E49]">
                        Online payment isn&rsquo;t available for this account yet
                      </p>
                      <p className="mt-2 text-xs leading-relaxed text-slate-600">
                        Your balance is shown below and is up to date. While we finish moving to our new
                        system, payments for this account need to be taken over the phone.
                        {theme?.supportPhone ? ' Please call our office and we can take it right away.' : ''}
                      </p>
                      {theme?.supportPhone && (
                        <p className="mt-3 text-sm text-slate-600">
                          Phone:{' '}
                          <a
                            className="font-semibold text-[#71001F] underline underline-offset-2 hover:opacity-80"
                            href={telHref(theme.supportPhone)}
                            aria-label={`Call our office at ${theme.supportPhone}`}
                          >
                            {theme.supportPhone}
                          </a>
                        </p>
                      )}
                    </div>
                  )}

                  <UserInfoHeader
                    key={`user-header-${pendingPaymentsRef.current.length}-${JSON.stringify(pendingPaymentsRef.current.map(p => p.id))}`}
                    userInfo={userInfo}
                    pendingPaymentsCount={pendingPaymentsRef.current.length}
                    payments={pendingPaymentsRef.current}
                    onLoadPayabliScript={handleUserHeaderPayNow}
                    isNewFlow={true}
                    scheduleFrequency={scheduledPayment.frequency}
                    setScheduleFrequency={(freq) => setScheduledPayment(prev => ({ ...prev, frequency: freq }))}
                    startDate={scheduledPayment.startDate}
                    setStartDate={(date) => setScheduledPayment(prev => ({ ...prev, startDate: date }))}
                    amountPerPayment={scheduledPayment.amountPerPayment}
                    setAmountPerPayment={(amount) => setScheduledPayment(prev => ({ ...prev, amountPerPayment: amount }))}
                    isScheduledPaymentValid={isScheduledPaymentValid}
                    getMinStartDate={getMinStartDate}
                    SCHEDULE_FREQUENCIES={SCHEDULE_FREQUENCIES}
                    pendingPaymentAmount={pendingPaymentAmount}
                    paymentDisabled={onlinePaymentUnavailable}
                  />

                  <PaymentAccordion
                    payments={visiblePayments}
                    userInfo={userInfo}
                    expandedPaymentId={expandedPaymentId}
                    onPaymentSelect={handlePaymentSelection}
                    onHideDetails={handleHideDetails}
                    loadPayabliScript={loadPayabliScript}
                  />
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="mb-6">
                    <svg 
                      className="mx-auto h-16 w-16 text-gray-400" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      aria-hidden="true"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Payments</h3>
                  <p className="text-gray-500 mb-6">There are no pending payments for your account at this time.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Refresh Page
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!onlinePaymentUnavailable && (
      <div className={`fixed bg-black/50 inset-0 flex justify-center items-center z-50 ${isOpen ? '' : 'hidden'}`} data-testid="payment-modal-overlay">
        <div className="w-[95%] max-w-sm sm:max-w-md mx-auto border-2 rounded-lg p-3 sm:p-6 bg-white shadow-lg relative modal-content" data-testid="payment-modal">
          <button
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-lg sm:text-xl p-2"
            onClick={closePopup}
            data-testid="close-payment-modal"
          >
            ✕
          </button>
          <h4 className="text-base sm:text-lg font-bold text-gray-700 mb-4 text-center pt-2" data-testid="payment-method-title">Payment Method</h4>

          <div className="tabs mb-4 flex border-b overflow-x-auto" data-testid="payment-tabs">
            <button
              className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                selectedPaymentTab === PAYMENT_TABS.CARD ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
              }`}
              onClick={() => setSelectedPaymentTab(PAYMENT_TABS.CARD)}
              data-testid="payment-card-tab"
            >
              Payment Card
            </button>
            <button
              className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
              }`}
              onClick={() => setSelectedPaymentTab(PAYMENT_TABS.BANK_DEBIT)}
              data-testid="bank-debit-tab"
            >
              Bank Debit
            </button>
            {/* STI-2495 (review R1): the wallet flow can't produce a vaultable method, so a scheduled
                payment on it would vault a one-time token and fail at cycle 2 with E7007. Don't offer
                Wallet for a scheduled payment at all — a recurring enrolment needs Card or Bank Debit. */}
            {!scheduledPayment.isScheduled && (
              <button
                className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                  selectedPaymentTab === PAYMENT_TABS.WALLET ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
                }`}
                onClick={() => setSelectedPaymentTab(PAYMENT_TABS.WALLET)}
                data-testid="wallet-tab"
              >
                Wallets
              </button>
            )}
          </div>

          <div className="min-h-[300px] sm:min-h-[400px]" data-testid="payment-form-container">
            <div id="pay-component-card" data-testid="payabli-component"></div>
          </div>
          {
            <button
              id="btn-pay"
              data-testid="pay-button"
              className={`w-full py-2.5 sm:py-3 bg-gradient-to-r ${theme["gradient-home-button-text"]} text-white font-bold text-sm sm:text-base uppercase rounded-md flex items-center justify-center mt-4 button-hover ${selectedPaymentTab === PAYMENT_TABS.WALLET ? 'hidden' : ''} ${
                !isScheduledPaymentValid() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={!isScheduledPaymentValid()}
              onClick={handlePayClick}
            >
              <img
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAPxJREFUSEvtldGNwjAMhr9/AnQTAJNwbMAG9CZATAC3ARvABjACN8mVDW4DI6Mg9VqStEWVeMAvjSrb3x8ndsTApoHz0wpgZgtgBXwGQWfgW5J/k5YFmNkBWEayOGSbIiQBQfkxJFgDp7AugE1Yz1M7yQG8BDNgLWlXVWpmrtwhP5LupWtsJgewEPEh6a8GmAC//k9SNE8rQCyBmd0EDAbI3aAbPOXURmEO0gCYmR/YHvAad7ES+KrfqEcAdxx3yVzxLSVNq7GPAPeb04tRP/A3oNm5taZ7l+g1S+RTc9SrCeAi6d8EiI0Kf8W6dvMFKLKjoqfyaFj2TX4WeAX0MmsZ8nx06AAAAABJRU5ErkJggg=="
                alt="Save Payment Icon"
                className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
              />
              {scheduledPayment.isScheduled ? 'Schedule Payment' : 'Pay'}
            </button>
          }
        </div>
      </div>
      )}
    </div>
  );
};

export default LinkPayment;
